// ============================================================================
// OUTPUT VIEWER MODAL COMPONENT
// Modal for viewing output content with format-specific rendering
// ============================================================================

import React from 'react';
import { ProjectOutput, OutputContent } from '../../hooks/useOutputs';
import ReactMarkdown from 'react-markdown';

interface OutputViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  output: ProjectOutput | null;
  content: OutputContent | null;
  isLoading: boolean;
  onDownload: (output: ProjectOutput) => void;
}

export const OutputViewerModal: React.FC<OutputViewerModalProps> = ({
  isOpen,
  onClose,
  output,
  content,
  isLoading,
  onDownload
}) => {
  if (!isOpen || !output) return null;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading content...</p>
        </div>
      );
    }

    if (!content) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Failed to load content</p>
        </div>
      );
    }

    switch (content.format) {
      case 'json':
        return <JSONViewer data={content.content} />;
      case 'markdown':
        return <MarkdownViewer content={content.content} />;
      case 'csv':
        return <CSVViewer content={content.content} />;
      default:
        return (
          <div style={{ padding: '20px' }}>
            <p>Unsupported format: {content.format}</p>
            <p>Please download the file to view it.</p>
          </div>
        );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          border: '2px solid #00c6c2',
          borderRadius: '15px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 30px',
            borderBottom: '2px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2 style={{ color: '#052831', margin: 0, marginBottom: '5px' }}>
              {output.name}
            </h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  background: '#e3f2fd',
                  color: '#1976d2',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                {output.format.toUpperCase()}
              </span>
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  background: '#f3e5f5',
                  color: '#7b1fa2',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                {output.type}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#052831',
              padding: '0 10px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 30px'
          }}
        >
          {renderContent()}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '15px 30px',
            borderTop: '2px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
          }}
        >
          <button
            onClick={() => onDownload(output)}
            style={{
              padding: '10px 24px',
              background: '#00c6c2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Download
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#f5f5f5',
              color: '#052831',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// JSON Viewer Component
const JSONViewer: React.FC<{ data: any }> = ({ data }) => {
  return (
    <pre
      style={{
        background: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '0.9rem',
        lineHeight: '1.5'
      }}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

// Markdown Viewer Component
const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
  return (
    <div
      style={{
        padding: '10px',
        lineHeight: '1.6'
      }}
      className="markdown-content"
    >
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
};

// CSV Viewer Component
const CSVViewer: React.FC<{ content: string }> = ({ content }) => {
  // Simple CSV parsing - split by lines and commas
  const lines = content.split('\n').filter(line => line.trim());
  const headers = lines[0]?.split(',') || [];
  const rows = lines.slice(1).map(line => line.split(','));

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}
      >
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={header.trim()}
                style={{
                  padding: '12px',
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  textAlign: 'left',
                  fontWeight: 600
                }}
              >
                {header.trim()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]?.trim() || row.join(',')}>
              {row.map((cell) => (
                <td
                  key={cell.trim()}
                  style={{
                    padding: '12px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  {cell.trim()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
